import Debug from 'debug';
import { createDraft, finishDraft, isDraft } from 'immer';
import { merge } from 'lodash';
import {
  createEditor,
  Element,
  Transforms,
  Range,
  Node,
  Editor,
  type Operation,
  type Descendant,
  type DecoratedRange,
} from 'slate';

import type {
  EventHandler,
  MarkPlugin,
  EditorPlugin,
  LeafElement,
  MarkdownPlugin,
  OperationFilter,
  ComponentProps,
  ComponentPlugin,
  HandlersPlugin,
  DecoratorPlugin,
  NormalizationPlugin,
  InsertDataPlugin,
  SchemaPlugin,
  OperationHandlerPlugin,
  TransformPlugin,
  PasteTransformPlugin,
  ProviderPlugin,
  LayoutComponentPlugin,
  LayoutComponentLocation,
  ProviderForType,
  ProviderType,
  EditorEventType,
  EventHandlers,
  EventHandlerCallbackType,
  DocumentValue,
  MetadataNormalization,
  EditorPluginFactory,
  CustomElement,
  ToolbarItems,
} from './types/aha-editor';
import EditorState, { Namespace, AhaEditorKey } from './util/EditorState';
import type AhaEditorV2Controller from './controllers/AhaEditorV2Controller';
import History from './collaboration/History';
import './scrubber.ts';
import Annotations from './plugins/AnnotationsPlugin/Annotations';
import { encodeFragment } from './util/fragment';
import runAfterSlate from './util/runAfterSlate';
import { fixupRefs } from './ot-slate-v2';
import Normalization from './normalization';
import { ReactEditor } from 'slate-react';

const debug = Debug('aha-editor:plugins');

export type NodeDecorationsMap = Map<Element, Range[]>;

type AhaEditorEventHandlers = {
  [E in EditorEventType]: Array<EventHandlers[E]>;
};

export interface AhaEditorSchema {
  inlineElementTypes: string[];
  voidElementTypes: string[];
}

const LAYOUT_COMPONENT_LOCATIONS: LayoutComponentLocation[] = [
  'above',
  'below',
  'left',
  'right',
  'above_content',
  'below_content',
];

export class AhaEditor {
  private _editor: Editor;

  // NOTE: The controller is not always defined, for example when used for serialization
  // Changing this type to `| undefined` would be nice, but involves changes to a lot of existing code.
  controller: AhaEditorV2Controller;

  plugins = new Map<string, EditorPlugin>();

  components: {
    [type: string]: React.ComponentType<ComponentProps>;
  } = {};

  // The difference between operation filters and operation handlers
  // is that operation filters will be called for every operation applied
  // to the editor. It is useful when we want to keep track of what's
  // going on in the editor (e.g. when working with history).
  // Operation handlers on the other hand are used to respond to a single
  // operation type, either to augment an existing operation or to define
  // new operations (e.g. add_annotation).
  // Operation handlers run before operation filters.
  operationFilters: OperationFilter[] = [];

  // Operation handlers are only triggered through editor.apply. Transforms.transform only
  // runs the default handlers.
  operationHandlers: {
    [operation: string]: OperationHandlerPlugin[];
  } = {};

  decorators: DecoratorPlugin[] = [];

  marks: {
    [name: string]: MarkPlugin;
  } = {};

  handlers: AhaEditorEventHandlers = {
    onInit: [],
    onDestroy: [],
    onMount: [],
    onClick: [],
    onKeyDown: [],
    onDragStart: [],
    onDrop: [],
    onCopy: [],
    onCut: [],
    onPaste: [],
    onChange: [],
    onSelectionUpdate: [],
    onMouseDown: [],
    onFocus: [],
    onBlur: [],
    onBodyFocus: [],
    onBodyBlur: [],
    onCompositionStart: [],
    onCompositionEnd: [],
  };

  normalizations: NormalizationPlugin[] = [];
  metadataNormalizations: MetadataNormalization[] = [];

  markdown: MarkdownPlugin[] = [];

  schema: AhaEditorSchema = {
    inlineElementTypes: [],
    voidElementTypes: [],
  };

  transformers: {
    [T in string]: TransformPlugin<T>[];
  } = {};

  pasteTransformers: {
    [T in string]: PasteTransformPlugin<T>[];
  } = {};

  registeredPlugins: Set<EditorPlugin> = new Set<EditorPlugin>();

  insertDataHandlers: InsertDataPlugin[] = [];

  elementToolbarItems = new Map<CustomElement['type'], ToolbarItems>();

  providers: Partial<Record<ProviderType, ProviderPlugin>> = {};

  layoutComponents: Record<LayoutComponentLocation, LayoutComponentPlugin[]> = {
    above: [],
    below: [],
    above_content: [],
    below_content: [],
    left: [],
    right: [],
  };

  // TODO: There is a typescript problem to resolve here. Sometimes `editor`
  // is a plain Editor instance, and sometimes a ReactEditor.
  constructor(editor = createEditor(), controller?: AhaEditorV2Controller) {
    this.editor = editor;
    if (controller) {
      this.controller = controller;
    }

    EditorState.set(editor, Namespace.AhaEditor, AhaEditorKey.Instance, this);
  }

  destroy() {
    // Give providers an opportunity to clean themselves up.
    for (const [, provider] of Object.entries(this.providers)) {
      if ('onDestroy' in provider) {
        provider.onDestroy?.();
      }
    }

    // Call onDestroy handlers
    this.handlers.onDestroy.forEach((handler) => handler(this));
  }

  get editor(): Editor {
    return this._editor;
  }

  set editor(editor: Editor) {
    const { apply, insertData } = editor;

    Normalization.initializeNormalization(
      editor,
      this.normalizations,
      this.metadataNormalizations,
      this.schema
    );

    editor.apply = (op: Operation) => {
      return this.applyOperation(op, apply);
    };

    editor.insertData = (data: DataTransfer) => {
      // InsertData is only available for react editors.
      if (!this.insertData(data) && insertData) {
        insertData(data);
      }
    };

    // Enable chunking for performance https://docs.slatejs.org/walkthroughs/09-performance#enable-chunking-experimental
    editor.getChunkSize = (node: Node) => (Editor.isEditor(node) ? 1000 : null);

    this._editor = editor;
  }

  // Return the AhaEditor instance that contains the specified editor (if any).
  static getForEditor(editor: Editor): AhaEditor | undefined {
    const ahaEditor = EditorState.get(
      editor,
      Namespace.AhaEditor,
      AhaEditorKey.Instance
    );

    if (!ahaEditor) {
      console.warn('No AhaEditor found for the provided editor instance');
    }
    return ahaEditor;
  }

  static withProvider(
    editor: Editor,
    providerName: ProviderPlugin['type'],
    callback: (provider: ProviderPlugin) => void
  ) {
    const ahaEditor = AhaEditor.getForEditor(editor);
    if (!ahaEditor) return;

    const provider = ahaEditor.getProvider(providerName);
    if (provider) {
      callback(provider);
    }
  }

  static clipboardFormatKey = 'x-aha-v2-fragment';

  private registerPlugin(plugin: EditorPlugin | EditorPluginFactory) {
    if (this.registeredPlugins.has(plugin)) {
      debug(`Skipping. Plugin ${plugin.name}, had already been registered`);
      return;
    }

    // Add the plugin to the registered plugins map  before its dependencies to avoid
    // potential trouble with circular dependencies.
    this.registeredPlugins.add(plugin);

    if (typeof plugin === 'function') {
      plugin = plugin();
    }

    if (plugin.id) {
      if (this.plugins.has(plugin.id)) {
        debug(
          `A plugin with id ${plugin.id} is already registered. Replacing with the new one.`
        );
      }
      this.plugins.set(plugin.id, plugin);
    }

    if (plugin.dependencies) {
      for (const dependency of plugin.dependencies) {
        if (!this.registeredPlugins.has(dependency)) {
          debug(
            `Registering Plugin ${dependency.name}, as a dependency of ${plugin.name}`
          );
          this.registerPlugin(dependency);
        }
      }
    }

    if (plugin.marks) {
      this.registerMarks(plugin.marks);
    }

    if (plugin.mark) {
      this.registerMark(plugin.mark);
    }

    if (plugin.components) {
      this.registerComponents(plugin.components);
    }

    if (plugin.component) {
      this.registerComponent(plugin.component);
    }

    if (plugin.handlers) {
      this.registerHandlers(plugin.handlers);
    }

    if (plugin.operationHandlers) {
      this.registerOperationHandlers(plugin.operationHandlers);
    }

    if (plugin.operationHandler) {
      this.registerOperationHandler(plugin.operationHandler);
    }

    if (plugin.operationFilter) {
      this.registerOperationFilter(plugin.operationFilter);
    }

    if (plugin.decorators) {
      this.registerDecorators(plugin.decorators);
    }

    if (plugin.decorator) {
      this.registerDecorator(plugin.decorator);
    }

    if (plugin.normalizations) {
      this.registerNormalizations(plugin.normalizations);
    }

    if (plugin.normalization) {
      this.registerNormalization(plugin.normalization);
    }

    if (plugin.markdown) {
      this.registerMarkdown(plugin.markdown);
    }

    if (plugin.schema) {
      this.registerSchema(plugin.schema);
    }

    if (plugin.transformer) {
      this.registerTransformer(plugin.transformer);
    }

    if (plugin.pasteTransformer) {
      this.registerPasteTransformer(plugin.pasteTransformer);
    }

    if (plugin.insertData) {
      this.registerInsertData(plugin.insertData);
    }

    if (plugin.setPluginState && this.controller?.state?.pluginState) {
      plugin.setPluginState(this.controller.state.pluginState, this.editor);
    }

    if (plugin.provider) {
      this.registerProviderPlugin(plugin.provider);
    }

    if (plugin.layoutComponent) {
      this.registerLayoutComponentPlugin(plugin.layoutComponent);
    }

    if (plugin.metadataNormalizations) {
      this.metadataNormalizations = this.metadataNormalizations.concat(
        plugin.metadataNormalizations
      );
    }
  }

  register(plugin: EditorPlugin | EditorPlugin[]) {
    const plugins = Array.isArray(plugin) ? plugin : [plugin];

    // Flattening the plugins array allows for simple plugin composition
    // where an array of plugins is also a plugin itself.
    for (const p of plugins.flat(10)) {
      this.registerPlugin(p);
    }

    // After all the plugins are registered, loop through and call the onInit handlers on each
    this.runOnInitHandlers();
  }

  // Marks
  private getMarks(leaf: LeafElement): MarkPlugin[] {
    const { text, ...props } = leaf;

    return Object.entries(props)
      .map(([prop, value]): MarkPlugin | null => {
        if (value === null || value === false || typeof value === 'undefined') {
          return null;
        }
        return this.marks[prop];
      })
      .filter(Boolean);
  }

  applyMarks(leaf: LeafElement) {
    const marks = this.getMarks(leaf);

    let newProps: Record<string, unknown> = {};
    for (const mark of marks) {
      let markProps: Record<string, unknown>;
      if (mark.use) {
        markProps = mark.use({
          props: newProps,
          mark,
          leaf,
          defaultUse: this.defaultUseMark,
        });
      } else {
        markProps = this.defaultUseMark(mark, newProps);
      }

      newProps = { ...newProps, ...markProps };
    }

    return newProps;
  }

  private registerMark(mark: MarkPlugin) {
    debug(`Registering mark: ${JSON.stringify(mark)}`);

    if (!Object.hasOwn(mark, 'type')) {
      throw new Error('Mark must have a "type" property.');
    }

    if (!Object.hasOwn(mark, 'use') && !Object.hasOwn(mark, 'props')) {
      throw new Error(
        'Mark must have at least one of the following properties: "use", "props".'
      );
    }

    if (mark.use && typeof mark.use !== 'function') {
      throw new Error('The "use" property must be a function.');
    }

    this.marks[mark.type] = mark;
  }

  private registerMarks(marks: MarkPlugin[]) {
    if (!Array.isArray(marks)) {
      throw new Error('Marks must be an array.');
    }

    for (const mark of marks) {
      this.registerMark(mark);
    }
  }

  private defaultUseMark(mark: MarkPlugin, props: Record<string, unknown>) {
    const newProps = {
      ...props,
    };

    for (const propName in mark.props) {
      // Do a shallow merge of object props, replace any other props.
      if (typeof props[propName] === 'object') {
        newProps[propName] = {
          ...(props[propName] as object),
          ...(mark.props[propName] as object),
        };
      } else if (propName === 'className') {
        // Concatenate classnames
        const currentClass = (newProps[propName] as string) || '';
        newProps[propName] = `${currentClass} ${mark.props[propName]}`.trim();
      } else {
        newProps[propName] = mark.props[propName];
      }
    }

    return newProps;
  }

  // Components
  private registerComponents(components: ComponentPlugin[]) {
    if (!Array.isArray(components)) {
      throw new Error('Components must be an array.');
    }

    for (const component of components) {
      this.registerComponent(component);
    }
  }

  private registerComponent(component: ComponentPlugin) {
    debug(`Registering component: ${JSON.stringify(component)}`);

    if (!component.type) {
      throw new Error('Component must contain a "type" property.');
    }

    if (!component.component) {
      throw new Error('Component must contain a "component" property.');
    }

    this.components[component.type] = component.component;
    if (component.toolbarItems) {
      this.elementToolbarItems.set(component.type, component.toolbarItems);
    }
  }

  getComponent(type: string) {
    // TODO: Log missing component if it doesn't exist
    const component = this.components[type];
    if (!component && type !== 'paragraph') {
      console.warn(`Component for "${type}" was not found.`);
    }
    return component;
  }

  transformElement<T extends Element>(element: T): T {
    const transformers = this.transformers[element.type];
    if (!transformers) {
      return element;
    }

    return transformers.reduce(
      (acc, transformer) => transformer.transform(acc),
      element
    );
  }

  transformPastedFragment(
    fragment: Element[],
    data: DataTransfer | null
  ): Element[] {
    if (Object.keys(this.pasteTransformers).length === 0) {
      return fragment;
    }

    const transformNode = (node: Descendant) => {
      if (Element.isElement(node)) {
        for (const transformer of this.pasteTransformers[node.type] ?? []) {
          node = transformer.transform(
            this.editor,
            this.controller,
            node,
            Boolean(this.controller?.isMove(data))
          );
        }

        if (node.children) {
          node.children = node.children.map(transformNode);
        }
      }
      return node;
    };

    return fragment.map(transformNode) as Element[];
  }

  /**
   * Replaces content without using collaboration. Useful for replacing content with a template
   * when not using a collaborative editor
   */
  replaceContentWithoutCollaboration(value: DocumentValue) {
    const { editor } = this;

    editor.children = value.children;
    editor.metadata = value.metadata;

    // Manually trigger the onChange handler
    this.runOnChangeHandlers();

    this.controller.actionReRenderEditor();
  }

  // Event Handlers
  private registerHandlers(handlers: Partial<HandlersPlugin>) {
    for (const [name, candidate] of Object.entries(handlers)) {
      if (!(name in this.handlers)) {
        console.warn(
          `Attempting to register handler for unsupported event: ${name}.`
        );
        continue;
      }

      let handlersList: EventHandlerCallbackType[];
      if (Array.isArray(candidate)) {
        handlersList = candidate;
      } else {
        handlersList = [candidate];
      }

      for (const handler of handlersList) {
        if (typeof handler !== 'function') {
          throw new Error('Handler must be a function.');
        }

        // @ts-expect-error Not all event handlers have the same signature
        this.handlers[name as EditorEventType].push(handler);
      }
    }
  }

  private runHandlers(handlers: EventHandler<any>[], event: Event): boolean {
    for (const handler of handlers) {
      const result = handler(event, this);
      if (result) {
        return true;
      }
    }
    return false;
  }

  private runOnInitHandlers() {
    // onInit handlers should only ever be called once, so reset in case more plugins are registered later
    const handlers = this.handlers.onInit;
    this.handlers.onInit = [];

    handlers.forEach((handler) => handler(this));
  }

  runOnChangeHandlers() {
    for (const handler of this.handlers.onChange) {
      handler(this);
    }
  }

  private clickHandler(event: MouseEvent): boolean {
    return this.runHandlers(this.handlers.onClick, event);
  }

  private keyDownHandler(event: KeyboardEvent): boolean {
    return this.runHandlers(this.handlers.onKeyDown, event);
  }

  private dragStartHandler(event: DragEvent): boolean {
    // Track this drag operation so we can identify it if it's dropped
    // into the same editor
    this.controller?.actionSetMoveId(event.dataTransfer);
    return this.runHandlers(this.handlers.onDragStart, event);
  }

  private dropHandler(event: DragEvent): boolean {
    return this.runHandlers(this.handlers.onDrop, event);
  }

  private copyHandler(event: ClipboardEvent): boolean {
    return this.runHandlers(this.handlers.onCopy, event);
  }

  private cutHandler(event: ClipboardEvent): boolean {
    event.preventDefault();

    const result = this.runHandlers(this.handlers.onCut, event);
    if (result) return result;

    if (!event.clipboardData) return false;
    const { editor } = this;

    // Execute Slate's default way of handling cut operations. This will ensure that
    // text/html and text/plain data is set on the clipboard.
    // We will overwrite the fragment data with our own custom data.
    ReactEditor.setFragmentData(editor, event.clipboardData, 'cut');

    const editorToCutFrom = Annotations.getEditorForCutting(editor);
    const fragment = editorToCutFrom.getFragment();

    event.clipboardData.setData(
      `application/${AhaEditor.clipboardFormatKey}`,
      encodeFragment(fragment)
    );

    // Track this cut operation so we can identify it if it's pasted
    // into the same editor
    this.controller?.actionSetMoveId(event.clipboardData);

    const { selection } = editor;
    if (selection) {
      if (Range.isExpanded(selection)) {
        Editor.deleteFragment(editor);
      } else {
        const node = Node.parent(editor, selection.anchor.path);
        if (Element.isElement(node) && Editor.isVoid(editor, node)) {
          Transforms.delete(editor);
        }
      }
    }
    return true;
  }

  private pasteHandler(event: ClipboardEvent): boolean {
    return this.runHandlers(this.handlers.onPaste, event);
  }

  private mouseDownHandler(event: MouseEvent): boolean {
    return this.runHandlers(this.handlers.onMouseDown, event);
  }

  private focusHandler(event: FocusEvent): boolean {
    if (this.controller) {
      // Check that the focus target is actually in the editor DOM. This might seem unintuitive, but it happens
      // when whiteboards go full screen, because it's rendered in a React portal, the whiteboard's "focus" bubbles
      // up to the editor, even though it's not directly in the editor's DOM.
      if (!this.focusEditorForElement(event.target)) {
        return false;
      }

      this.controller.dispatchEvent('aha-editor:focus');

      runAfterSlate(() => {
        this.controller.state.focused = true;
      });
    }
    return this.runHandlers(this.handlers.onFocus, event);
  }

  private focusEditorForElement(el: EventTarget | null) {
    const { editorContainer, toolbarContainer } = this.controller;

    return (
      editorContainer &&
      el instanceof HTMLElement &&
      (editorContainer.contains(el) || toolbarContainer.contains(el))
    );
  }

  private blurHandler(event: FocusEvent): boolean {
    if (this.controller) {
      const { relatedTarget } = event;

      // Editor container still contains the focused element, so we don't need to blur
      if (this.focusEditorForElement(relatedTarget)) {
        return false;
      }

      this.controller.dispatchEvent('aha-editor:blur', event);

      runAfterSlate(() => {
        this.controller.state.focused = false;
      });
    }

    return this.runHandlers(this.handlers.onBlur, event);
  }

  private compositionStartHandler(event: CompositionEvent): boolean {
    return this.runHandlers(this.handlers.onCompositionStart, event);
  }

  private compositionEndHandler(event: CompositionEvent): boolean {
    return this.runHandlers(this.handlers.onCompositionEnd, event);
  }

  private bodyFocusHandler(event: FocusEvent): boolean {
    return this.runHandlers(this.handlers.onBodyFocus, event);
  }

  private bodyBlurHandler(event: FocusEvent): boolean {
    return this.runHandlers(this.handlers.onBodyBlur, event);
  }

  eventHandlers = {
    onClick: (event: MouseEvent) => this.clickHandler(event),
    onKeyDown: (event: KeyboardEvent) => this.keyDownHandler(event),
    onDragStart: (event: DragEvent) => this.dragStartHandler(event),
    onDrop: (event: DragEvent) => this.dropHandler(event),
    onCopy: (event: ClipboardEvent) => this.copyHandler(event),
    onCut: (event: ClipboardEvent) => this.cutHandler(event),
    onPaste: (event: ClipboardEvent) => this.pasteHandler(event),
    onMouseDown: (event: MouseEvent) => this.mouseDownHandler(event),
    onBlur: (event: FocusEvent) => this.bodyBlurHandler(event),
    onFocus: (event: FocusEvent) => this.bodyFocusHandler(event),
    onCompositionStart: (event: CompositionEvent) =>
      this.compositionStartHandler(event),
    onCompositionEnd: (event: CompositionEvent) =>
      this.compositionEndHandler(event),
  } as const;

  editorContainerEventHandlers = {
    onFocus: (event: FocusEvent) => this.focusHandler(event),
    onBlur: (event: FocusEvent) => this.blurHandler(event),
  } as const;

  private runOperationHandlers(op: Operation) {
    if (!Object.hasOwn(this.operationHandlers, op.type)) return;

    const handlers = this.operationHandlers[op.type];
    this.editor.children = createDraft(this.editor.children);
    const selection =
      this.editor.selection && createDraft(this.editor.selection);
    try {
      for (const handler of handlers) {
        const result = handler.run(this.editor, op);
        if (result) break;
      }
    } finally {
      this.editor.children = finishDraft(this.editor.children);
      if (selection) {
        this.editor.selection = isDraft(selection)
          ? finishDraft(selection)
          : selection;
      } else {
        this.editor.selection = null;
      }
    }
  }

  private applyOperation(op: Operation, baseApply: (op: Operation) => void) {
    if (
      !op.collaborative &&
      this.controller?.canApply &&
      !this.controller.canApply(op)
    ) {
      // Ignore local ops that can't be applied
      return;
    }

    fixupRefs(this.editor, op);
    this.runOperationHandlers(op);

    const history = History.getForEditor(this.editor);
    if (history && this.controller && !this.controller.isCollaborative) {
      history.save(this.editor, op);
    }

    // Run operation filters
    let i = 0;
    const filters = this.operationFilters;
    const next = (editor: Editor, op: Operation) => {
      if (i >= filters.length) {
        return baseApply(op);
      }

      const fn = filters[i++];
      return fn(editor, op, next);
    };

    return next(this.editor, op);
  }

  private registerOperationFilter(operationFilter: OperationFilter) {
    if (typeof operationFilter !== 'function') {
      throw new Error('Operation filter must be a function.');
    }

    this.operationFilters.push(operationFilter);
  }

  private registerOperationHandlers(
    operationHandlers: OperationHandlerPlugin[]
  ) {
    if (!Array.isArray(operationHandlers)) {
      throw new Error('Operation handlers must be an array.');
    }

    for (const operationHandler of operationHandlers) {
      this.registerOperationHandler(operationHandler);
    }
  }

  private registerOperationHandler(operationHandler: OperationHandlerPlugin) {
    if (!operationHandler.operation) {
      throw new Error('OperationHandler must have an "operation" property.');
    }

    if (!operationHandler.run) {
      throw new Error('OperationHandler must have a "run" property.');
    }

    if (typeof operationHandler.run !== 'function') {
      throw new Error('OperationHandler "run" must be a function.');
    }

    const { operation } = operationHandler;
    if (!Object.hasOwn(this.operationHandlers, operation)) {
      this.operationHandlers[operation] = [];
    }

    this.operationHandlers[operation].push(operationHandler);
  }

  // Decorators
  private registerDecorator(decorator: DecoratorPlugin) {
    if (!Object.hasOwn(decorator, 'decorate')) {
      throw new Error('Decorator must have a "decorate" property.');
    }

    if (typeof decorator.decorate !== 'function') {
      throw new Error('Decorate must be a function.');
    }

    this.decorators.push(decorator);
  }

  private registerDecorators(decorators: DecoratorPlugin[]) {
    if (!Array.isArray(decorators)) {
      throw new Error('Decorators must be an array.');
    }

    for (const decorator of decorators) {
      this.registerDecorator(decorator);
    }
  }

  // Compute the decorations for the whole editor.
  computeDecorations(): NodeDecorationsMap {
    const nodeToDecorations = new Map<Element, DecoratedRange[]>();

    const decorateNode = (
      node: Element,
      range: Range,
      decorations: Record<string, any>
    ) => {
      if (!nodeToDecorations.has(node)) {
        nodeToDecorations.set(node, []);
      }

      // Merge the leaf element's decorations with the incoming ones
      // instead of completely replacing them.
      const mergeDecorations = (
        leaf: LeafElement,
        toMerge: Record<string, any>
      ) => {
        if (!toMerge.decorations) return;

        if (!leaf.decorations) {
          leaf.decorations = {};
        }

        merge(leaf.decorations, toMerge.decorations);
      };

      // Optional chain operator here to appease TypeScript even though
      // we know that nodeToDecorations.get(node) will never be undefined.
      nodeToDecorations.get(node)?.push({
        ...range,
        decorations,
        merge: mergeDecorations,
      });
    };

    this.decorators.forEach((decorator) =>
      decorator.decorate(this.editor, decorateNode)
    );

    return nodeToDecorations;
  }

  private registerNormalization(normalization: NormalizationPlugin) {
    if (!normalization.check && !normalization.type) {
      throw new Error('Normalization must have a "check" or "type" property.');
    }

    if (!Object.hasOwn(normalization, 'run')) {
      throw new Error('Normalization must have a "run" property.');
    }

    if (typeof normalization.run !== 'function') {
      throw new Error('Run must be a function.');
    }

    this.normalizations.push(normalization);
  }

  private registerNormalizations(normalizations: NormalizationPlugin[]) {
    if (!Array.isArray(normalizations)) {
      throw new Error('Normalizations must be an array.');
    }

    for (const normalization of normalizations) {
      this.registerNormalization(normalization);
    }
  }

  private registerMarkdown(config: MarkdownPlugin[]) {
    if (!Array.isArray(config)) {
      throw new Error('Markdown configuration must be an array.');
    }

    for (const element of config) {
      this.registerMarkdownElement(element);
    }
  }

  private registerMarkdownElement(element: MarkdownPlugin) {
    if (!element.shortcut && !element.shortcuts) {
      throw new Error(
        'Markdown element must have at least one of the following properties: "shortcut", "shortcuts".'
      );
    }

    if (!element.type) {
      throw new Error('Markdown element must have a "type" property.');
    }

    if (
      element.type === 'block' &&
      (!element.convert || typeof element.convert !== 'function')
    ) {
      throw new Error(
        'Block markdown elements must have a "convert" function property.'
      );
    }

    if (
      element.type === 'block' &&
      (!element.revert || typeof element.revert !== 'function')
    ) {
      throw new Error(
        'Block markdown elements must have a "revert" function property.'
      );
    }

    if (element.type === 'mark' && !element.mark) {
      throw new Error('Mark markdown elements must have a "mark" property');
    }

    if (
      element.type === 'block' &&
      element.test &&
      typeof element.test !== 'function'
    ) {
      throw new Error(
        'Markdown element\'s "test" property must be a function if provided.'
      );
    }

    let shortcuts: (string | RegExp)[] = element.shortcuts ?? [];
    if (element.shortcut) {
      shortcuts = [element.shortcut];
    }

    if (element.shortcuts && !Array.isArray(element.shortcuts)) {
      throw new Error('Markdown element shortcuts must be an array.');
    }

    for (const shortcut of shortcuts) {
      this.markdown.push({
        shortcut,
        ...element,
      });
    }
  }

  private registerSchema(schemaPlugin: SchemaPlugin) {
    for (const type in schemaPlugin) {
      const schemaEntry = schemaPlugin[type];
      if (schemaEntry.isInline) {
        this.schema.inlineElementTypes.push(type);
      }
      if (schemaEntry.isVoid) {
        this.schema.voidElementTypes.push(type);
      }
    }
  }

  private registerTransformer(transformPlugin: TransformPlugin<string>) {
    this.transformers[transformPlugin.type] ??= [];
    this.transformers[transformPlugin.type].push(transformPlugin);
  }

  private registerPasteTransformer(
    pasteTransformPlugin: PasteTransformPlugin<string>
  ) {
    if (!pasteTransformPlugin.type) {
      throw new Error('PasteTransformPlugin must specify a type property');
    }

    if (typeof pasteTransformPlugin.transform !== 'function') {
      throw new Error('PasteTransformPlugin transform must be a function');
    }

    this.pasteTransformers[pasteTransformPlugin.type] ??= [];
    this.pasteTransformers[pasteTransformPlugin.type].push(
      pasteTransformPlugin
    );
  }

  private registerInsertData(insertDataPlugin: InsertDataPlugin) {
    if (typeof insertDataPlugin !== 'function') {
      throw new Error('Insert data must be a function');
    }

    this.insertDataHandlers.push(insertDataPlugin);
  }

  private insertData(data: DataTransfer) {
    for (const insertDataFunc of this.insertDataHandlers) {
      if (insertDataFunc(this.editor, data, this.controller)) {
        this.controller?.actionClearMoveId();
        return true;
      }
    }

    this.controller?.actionClearMoveId();
    return false;
  }

  registerProviderPlugin(provider: ProviderPlugin) {
    if (!Object.hasOwn(provider, 'type')) {
      throw new Error('Provider must have a "type" property');
    }
    this.providers[provider.type] = provider;
  }

  private registerLayoutComponentPlugin(
    layoutComponent: LayoutComponentPlugin
  ) {
    if (!('location' in layoutComponent)) {
      throw new Error('Layout component must have a "location" property');
    }

    if (!LAYOUT_COMPONENT_LOCATIONS.includes(layoutComponent.location)) {
      const validLocationsList = LAYOUT_COMPONENT_LOCATIONS.join(',');
      throw new Error(
        `Invalid location value provided, it must be one of: ${validLocationsList}`
      );
    }

    if (!('Component' in layoutComponent)) {
      throw new Error('Layout component must have a "Component" property');
    }

    const { Component } = layoutComponent;
    if (
      // Component wrapped in ApplicationView (wrapped by React.memo)
      (!('type' in Component) || typeof Component.type !== 'function') &&
      // Regular React.FC
      typeof Component !== 'function'
    ) {
      throw new Error(
        "Layout component's 'Component' property must be a function (React component)"
      );
    }

    const { location } = layoutComponent;
    this.layoutComponents[location].push(layoutComponent);
  }

  getLayoutComponents(
    location: LayoutComponentLocation
  ): LayoutComponentPlugin[] {
    return this.layoutComponents[location];
  }

  getProvider<T extends ProviderType>(type: T): ProviderForType<T> | undefined {
    const provider = this.providers[type] as ProviderForType<T> | undefined;
    if (provider) {
      return provider;
    }
    return provider;
  }

  hasProvider(type: ProviderType) {
    return type in this.providers;
  }

  getPlugin<Plugin extends EditorPlugin>(id: string) {
    return this.plugins.get(id) as Plugin | undefined;
  }

  hasPlugin(id: string) {
    return this.plugins.has(id);
  }
}

export default AhaEditor;
